/**
 * P-15 签名密钥（docs/03-UIUX.md §5）。
 *
 * 按产品分组的密钥卡片列表。每个密钥显示 key_id、角色、状态、有效期窗口。
 * T3 危险操作（owner 专属，后端 @Roles('owner')）：
 * - 轮换（active leaf）：旧密钥 → retiring（90 天保留期），新叶 → active；
 *   公钥集重签需离线根私钥人工介入（requires_root_signature 恒为 true）。
 * - 应急吊销（任意非终态）：不走 retiring 缓冲，立即移出公钥集，可能误伤离线缓存。
 * - 下线（retiring）：保留期满后进入 retired 终态。
 */
import { useCallback, useState, type ReactNode } from 'react';
import { EmptyFirstRun, ErrorBlock } from '../components/DataState';
import { describeError } from '../api/client';
import {
  compromiseSigningKey,
  listProducts,
  listSigningKeys,
  retireSigningKey,
  rotateSigningKey,
  type SigningKey,
} from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { formatDate } from '../lib/format';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import { useAuth } from '../auth/AuthContext';
import type { Product } from '../api/types';

const STATUS_LABEL: Record<string, string> = {
  active: '使用中',
  retiring: '轮换中',
  retired: '已下线',
  compromised: '已吊销',
};

type DialogState =
  | { kind: 'rotate'; key: SigningKey }
  | { kind: 'first-key' }
  | { kind: 'compromise'; key: SigningKey }
  | { kind: 'retire'; key: SigningKey }
  | null;

export function SigningKeysPage() {
  const { currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';

  const products = useAsync(useCallback(() => listProducts(), []), []);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [dialog, setDialog] = useState<DialogState>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const keys = useAsync(
    useCallback(() => listSigningKeys(selectedProductId || undefined), [selectedProductId]),
    [selectedProductId],
  );

  const productList: readonly Product[] = products.state.kind === 'ready' ? products.state.data.items : [];
  const keyList: readonly SigningKey[] = keys.state.kind === 'ready' ? keys.state.data : [];

  /** 已选产品、密钥已加载、且不存在任何「可用私钥」的 active 叶密钥 → 兑换链路不可用 */
  const showPrivateKeyWarn =
    selectedProductId !== '' &&
    keys.state.kind === 'ready' &&
    keyList.length > 0 &&
    !keyList.some((k) => k.role === 'leaf' && k.status === 'active' && k.has_private_key);

  const reloadKeys = useCallback(() => {
    setNotice(null);
    keys.reload();
  }, [keys]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">签名密钥</h1>
          <p className="page-head__sub">
            按产品管理的 Ed25519 签名密钥。轮换 / 吊销 / 下线为 T3 危险操作，仅 owner 可执行。
          </p>
        </div>
      </div>

      {notice && (
        <div className="notice notice--ok" role="status">
          <Icon name="circle-check" size={ICON_SIZE.inline} />
          {notice}
        </div>
      )}

      {showPrivateKeyWarn && (
        <div className="notice notice--warn" role="alert">
          <Icon name="triangle-alert" size={ICON_SIZE.inline} />
          <div>
            <p>
              该产品尚未生成<strong>可用私钥</strong>，客户端兑换 / 激活授权码将返回 5002。
              请先对 active 叶密钥执行「轮换」，生成 KEK 加密的私钥。
            </p>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="filter-bar__group">
          <label className="filter-bar__label">产品</label>
          <select
            className="filter-bar__select"
            value={selectedProductId}
            onChange={(e) => { setSelectedProductId(e.target.value); setNotice(null); }}
          >
            <option value="">全部产品</option>
            {productList.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {keys.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(keys.state.error, '加载密钥')} onRetry={keys.reload} />
        </div>
      ) : keys.state.kind === 'loading' ? (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="skeleton-bar" style={{ height: 120 }} />
          <div className="skeleton-bar" style={{ height: 120 }} />
        </div>
      ) : keyList.length === 0 ? (
        <div className="table-frame">
          <EmptyFirstRun
            icon="key-round"
            description={selectedProductId ? '该产品暂无签名密钥。生成首条 active 叶密钥后，才能签发授权码和完成客户端兑换/激活。' : '暂无签名密钥。生成首条 active 叶密钥后，才能签发授权码和完成客户端兑换/激活。'}
            action={
              isOwner ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setDialog({ kind: 'first-key' })}
                >
                  <Icon name="refresh-cw" size={ICON_SIZE.inline} />
                  生成首条签名密钥
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          {keyList.map((key) => (
            <div key={key.id} className="signing-key-card">
              <div className="signing-key-card__head">
                <code className="signing-key-card__kid">{key.key_id}</code>
                <span className={`status-dot status-dot--${key.status === 'active' ? 'active' : key.status === 'retiring' ? 'hold' : key.status === 'compromised' ? 'danger' : 'archived'}`} />
                <span className="signing-key-card__status">{STATUS_LABEL[key.status] ?? key.status}</span>
                <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                  {productList.find((p) => p.id === key.product_id)?.name ?? key.product_id.slice(0, 8)}
                </span>
              </div>
              <div className="signing-key-card__meta">
                <span className="signing-key-card__meta-item">{key.role === 'root' ? '根密钥' : '叶密钥'}</span>
                <span className="signing-key-card__meta-item">
                  生效：{formatDate(key.not_before)}
                </span>
                <span className="signing-key-card__meta-item">
                  到期：{key.not_after ? formatDate(key.not_after) : '无'}
                </span>
                {key.retired_at !== null && (
                  <span className="signing-key-card__meta-item">下线：{formatDate(key.retired_at)}</span>
                )}
              </div>
              <div className="signing-key-card__body">
                <code className="signing-key-card__pubkey">{key.public_key_b64u.slice(0, 24)}…</code>
              </div>
              {isOwner && key.role === 'leaf' && (
                <div className="signing-key-card__actions">
                  {key.status === 'active' && (
                    <button type="button" className="btn btn--quiet" onClick={() => setDialog({ kind: 'rotate', key })}>
                      <Icon name="refresh-cw" size={ICON_SIZE.inline} />
                      轮换
                    </button>
                  )}
                  {key.status === 'retiring' && (
                    <button type="button" className="btn btn--quiet" onClick={() => setDialog({ kind: 'retire', key })}>
                      <Icon name="archive" size={ICON_SIZE.inline} />
                      下线
                    </button>
                  )}
                  {(key.status === 'active' || key.status === 'retiring') && (
                    <button
                      type="button"
                      className="btn btn--quiet btn--danger-text"
                      onClick={() => setDialog({ kind: 'compromise', key })}
                    >
                      <Icon name="octagon-x" size={ICON_SIZE.inline} />
                      应急吊销
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog?.kind === 'rotate' && (
        <RotateDialog
          key={dialog.key.id}
          signingKey={dialog.key}
          onClose={() => setDialog(null)}
          onSubmit={async (reason) => {
            const result = await rotateSigningKey(dialog.key.product_id, { reason });
            setNotice(
              `已轮换：新密钥 ${result.new_key.key_id} 生效；旧密钥进入 90 天保留期。` +
                (result.requires_root_signature ? ' 公钥集重签需离线根私钥人工介入。' : ''),
            );
            reloadKeys();
          }}
        />
      )}
      {dialog?.kind === 'first-key' && (
        <FirstKeyDialog
          products={productList}
          onClose={() => setDialog(null)}
          onSubmit={async (productId, reason) => {
            const result = await rotateSigningKey(productId, { reason });
            setNotice(
              `已生成首条签名密钥 ${result.new_key.key_id}，客户端 redeem/activate 现在可用。` +
                (result.requires_root_signature ? ' 公钥集重签需离线根私钥人工介入。' : ''),
            );
            reloadKeys();
          }}
        />
      )}
      {dialog?.kind === 'compromise' && (
        <CompromiseDialog
          key={dialog.key.id}
          signingKey={dialog.key}
          onClose={() => setDialog(null)}
          onSubmit={async (reason) => {
            const result = await compromiseSigningKey(dialog.key.id, reason);
            setNotice(
              `已吊销 ${result.key.key_id}，立即移出公钥集；约 ${result.estimated_affected_licenses} 个离线缓存可能受影响。`,
            );
            reloadKeys();
          }}
        />
      )}
      {dialog?.kind === 'retire' && (
        <RetireDialog
          key={dialog.key.id}
          signingKey={dialog.key}
          onClose={() => setDialog(null)}
          onSubmit={async () => {
            const result = await retireSigningKey(dialog.key.id);
            setNotice(`已下线 ${result.key_id}（retired 终态）。`);
            reloadKeys();
          }}
        />
      )}
    </>
  );
}

/* ────────────────────────── 对话框 ────────────────────────── */

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="panel__head">
          <h2 className="panel__title">{title}</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">{children}</div>
      </div>
    </div>
  );
}

function ReasonForm({
  description,
  confirmLabel,
  danger,
  onClose,
  onSubmit,
}: {
  description: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (!reason.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作失败');
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-muted">{description}</p>
      <div className="field">
        <label htmlFor="sk-reason">原因（必填，写入审计）</label>
        <input id="sk-reason" type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
        <button type="button" className={danger ? 'btn btn--danger' : 'btn btn--primary'} disabled={submitting || !reason.trim()} onClick={handle}>
          {submitting ? '处理中…' : confirmLabel}
        </button>
      </div>
    </>
  );
}

function RotateDialog({
  onClose,
  onSubmit,
}: {
  readonly signingKey: SigningKey;
  readonly onClose: () => void;
  readonly onSubmit: (reason: string) => Promise<void>;
}) {
  return (
    <DialogShell title="轮换签名密钥" onClose={onClose}>
      <ReasonForm
        description="旧叶密钥进入 90 天保留期（retiring），新叶密钥生效。根私钥离线保管，公钥集重签需人工介入，在完成前客户端不会看到新密钥。"
        confirmLabel="确认轮换"
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </DialogShell>
  );
}

function FirstKeyDialog({
  products,
  onClose,
  onSubmit,
}: {
  readonly products: readonly Product[];
  readonly onClose: () => void;
  readonly onSubmit: (productId: string, reason: string) => Promise<void>;
}) {
  const [selectedProductId, setSelectedProductId] = useState('');
  return (
    <DialogShell title="生成首条签名密钥" onClose={onClose}>
      <div className="field">
        <label>产品</label>
        <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)}>
          <option value="">选择产品…</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      <ReasonForm
        description="为该产品生成首条 active 叶密钥。客户端兑换/激活授权码需要此密钥签名。"
        confirmLabel="生成密钥"
        onClose={onClose}
        onSubmit={async (reason) => {
          if (!selectedProductId) return;
          await onSubmit(selectedProductId, reason);
        }}
      />
    </DialogShell>
  );
}

function CompromiseDialog({
  signingKey,
  onClose,
  onSubmit,
}: {
  signingKey: SigningKey;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  return (
    <DialogShell title="应急吊销密钥" onClose={onClose}>
      <ReasonForm
        danger
        description={
          <>
            将吊销 <code>{signingKey.key_id}</code> 并<strong>立即移出公钥集</strong>（不走 retiring 缓冲）。
            该密钥签发的所有离线缓存可能立即失效，可能误伤正常用户。
          </>
        }
        confirmLabel="确认吊销"
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </DialogShell>
  );
}

function RetireDialog({
  signingKey,
  onClose,
  onSubmit,
}: {
  signingKey: SigningKey;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作失败');
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title="下线签名密钥" onClose={onClose}>
      <p className="text-muted">
        将 <code>{signingKey.key_id}</code> 从 retiring 转为 retired 终态（不可逆）。保留期未满时后端会拒绝（9009）。
      </p>
      {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
        <button type="button" className="btn btn--danger" disabled={submitting} onClick={handle}>
          {submitting ? '处理中…' : '确认下线'}
        </button>
      </div>
    </DialogShell>
  );
}
